import React, { useEffect, useState } from "react";
import { useHistory, useRouteMatch } from "react-router-dom";
import {
  Paper,
  Button,
  Typography,
  TextField,
  Container,
  RadioGroup,
  FormControlLabel,
  Radio
} from "@material-ui/core";
import { useRequest } from "ahooks";
import SubmitButton from "@/components/commons/SubmitButton";
import useStyles from "@/styles/alarmStyle";
import { useTranslation } from "@/langs/useTranslation";
import { toast } from "@/apis/request";
import { Form, FormItem } from "@/components/Form";
import TextFieldWithCount from "@/components/TextFieldWithCount";
import StripedTitle from "@/components/StripedTitle";
import LoadingIndicator from "@/components/commons/LoadingIndicator";
import AlarmRuleCreator from "./AlarmRuleCreator";
import AlarmNotifyUsers from "./AlarmNotifyUsers";
import AlarmNotifyExtraUsers from "./AlarmNotifyExtraUsers";
import {
  alarmTemplateDetailApi,
  createAlarmTemplateApi,
  updateAlarmTemplateApi
} from "@/apis/apiCollection";
import HasPermission from "@/components/commons/HasPermission";

const Creation = () => {
  const { t } = useTranslation();
  const history = useHistory();
  const classes = useStyles();

  const [initialValues, setInitialValues] = useState({
    name: "",
    policies: [],
    notifier: [],
    extraNotifier: [],
    note: "",
    // deviceOfflineMonitor: 0,
    send_msg: 0
  });

  const detailMode = useRouteMatch({
    path: "/alarm/template/info/:id"
  });

  const editMode = useRouteMatch({
    path: "/alarm/template/edit/:id"
  });

  const match = detailMode || editMode;

  const { loading, runAsync } = useRequest(createAlarmTemplateApi, {
    manual: true
  });

  const { loading: detailLoading, runAsync: getDetail } = useRequest(
    async () => {
      const { detail } = await alarmTemplateDetailApi({ id: match.params.id });
      const notifier = [];
      const extraNotifier = [];
      detail.notifier?.forEach(item => {
        if (item.extra) {
          extraNotifier.push(item);
        } else {
          notifier.push(item);
        }
      });
      detail.policies.forEach(policy => {
        policy.ext = JSON.parse(policy.ext || "{}");
      });
      setInitialValues({
        ...detail,
        note: detail.note || "",
        notifier,
        extraNotifier
      });
    },
    {
      manual: true
    }
  );

  const { loading: updateLoading, runAsync: runUpdateAsync } = useRequest(
    updateAlarmTemplateApi,
    {
      manual: true
    }
  );

  const handleSubmit = async values => {
    const derivedValues = {
      ...values,
      notifier: values.notifier.concat(values.extraNotifier)
    };
    derivedValues.policies = derivedValues.policies.map(v => {
      return {
        ...v,
        ext: typeof v.ext == "object" ? JSON.stringify(v.ext) : v.ext
      };
    });
    delete derivedValues.extraNotifier;
    if (editMode) {
      derivedValues.id = editMode.params.id;
      await runUpdateAsync(derivedValues);
    } else {
      await runAsync(derivedValues);
    }
    history.goBack();
    toast.success(t({ defaultMessage: "操作成功" }));
  };

  useEffect(() => {
    if (match) {
      getDetail({ id: match.params.id });
    }
  }, []);

  return (
    <div className={classes.main}>
      <Paper elevation={0}>
        {detailLoading ? (
          <LoadingIndicator />
        ) : (
          <Form
            disabled={!!detailMode}
            initialValues={initialValues}
            onSubmit={handleSubmit}
          >
            <div className={classes.tableTop}>
              <Typography variant="h5">
                {editMode
                  ? t({ defaultMessage: "编辑报警模板" })
                  : detailMode
                  ? t({ defaultMessage: "报警模板详情" })
                  : t({ defaultMessage: "新增报警模板" })}
              </Typography>
              <div className={classes.rightAction}>
                {detailMode ? (
                  <HasPermission permission="alarm_policy_edit">
                    <Button
                      color="primary"
                      variant="contained"
                      onClick={() => {
                        history.replace(
                          `/alarm/template/edit/${match.params.id}`
                        );
                      }}
                    >
                      {t({ defaultMessage: "编辑" })}
                    </Button>
                  </HasPermission>
                ) : (
                  <SubmitButton
                    color="primary"
                    variant="contained"
                    loading={loading || updateLoading}
                  >
                    {t({ defaultMessage: "确定" })}
                  </SubmitButton>
                )}
                <Button
                  variant="outlined"
                  onClick={() => {
                    history.goBack();
                  }}
                >
                  {t({ defaultMessage: "取消" })}
                </Button>
              </div>
            </div>
            <Container maxWidth="md">
              <StripedTitle>{t({ defaultMessage: "基本信息" })}</StripedTitle>
              <FormItem
                row
                required
                labelBold
                label={t({ defaultMessage: "报警模块名称" })}
                name="name"
                rules={{
                  required: true,
                  message: t({ defaultMessage: "报警模块名称不能为空" })
                }}
              >
                <TextField
                  variant="outlined"
                  size="small"
                  fullWidth
                  inputProps={{ maxLength: 100 }}
                  placeholder={t({ defaultMessage: "请填写名称" })}
                />
              </FormItem>
              <FormItem
                row
                required
                labelBold
                label={t({ defaultMessage: "配置报警规则" })}
                name="policies"
                rules={[
                  {
                    strategy: "length",
                    message: t({ defaultMessage: "报警规则不能为空" })
                  },
                  {
                    validator: (value, rule) => {
                      if (
                        value.some(item => {
                          const v =
                            item.ext.value || item.ext.app || item.value;
                          return (
                            !item.alarm_policy_option_id ||
                            v == null ||
                            v === "" ||
                            !item.notice_upper_limit
                          );
                        })
                      ) {
                        throw new Error(rule.message);
                      }
                    },
                    message: t({ defaultMessage: "请将报警规则填写完整" })
                  },
                  {
                    validator: (value, rule) => {
                      const peripheralMonitors = value.filter(
                        item =>
                          typeof item.ext.value == "string" &&
                          item.ext.value.startsWith("USB\\")
                      );
                      const uniqueValues = new Set(
                        peripheralMonitors.map(item => item.ext.value)
                      );
                      if (uniqueValues.size !== peripheralMonitors.length) {
                        throw new Error(rule.message);
                      }
                    },
                    message: t({ defaultMessage: "打印机不能重复" })
                  },
                  {
                    validator: (value, rule) => {
                      if (
                        value.some(
                          item => parseFloat(item.notice_upper_limit) > 10
                        )
                      ) {
                        throw new Error(rule.message);
                      }
                    },
                    message: t({ defaultMessage: "通知上限次数不能超过10" })
                  }
                ]}
              >
                <AlarmRuleCreator />
              </FormItem>
              {/* <FormItem
                row
                labelBold
                label={t({ defaultMessage: "是否监控设备离线状态" })}
                name="deviceOfflineMonitor"
              >
                <RadioGroup row>
                  <FormControlLabel
                    disabled={!!detailMode}
                    value={1}
                    control={<Radio color="primary" />}
                    label={t({ defaultMessage: "是" })}
                  />
                  <FormControlLabel
                    disabled={!!detailMode}
                    value={0}
                    control={<Radio color="primary" />}
                    label={t({ defaultMessage: "否" })}
                  />
                </RadioGroup>
              </FormItem> */}
              <FormItem
                row
                labelBold
                label={t({ defaultMessage: "模板注解" })}
                name="note"
              >
                <TextFieldWithCount
                  variant="outlined"
                  fullWidth
                  multiline
                  maxRows={5}
                  inputProps={{ maxLength: 500 }}
                  placeholder={t({ defaultMessage: "请填写注解" })}
                />
              </FormItem>
              <StripedTitle
                subTitle={t({
                  defaultMessage: "我们会透过电子邮件、短信或系统通知您。"
                })}
              >
                {t({ defaultMessage: "报警通知对象" })}
              </StripedTitle>
              <FormItem
                row
                labelBold
                label={t({ defaultMessage: "公司成员" })}
                name="notifier"
                rules={{
                  strategy: "length",
                  message: t({ defaultMessage: "请添加报警通知对象" })
                }}
              >
                <AlarmNotifyUsers />
              </FormItem>
              <FormItem
                row
                labelBold
                label={t({ defaultMessage: "例外对象" })}
                name="extraNotifier"
              >
                <AlarmNotifyExtraUsers />
              </FormItem>
              {/* <FormItem
                row
                labelBold
                label={t({ defaultMessage: "是否同步发送短信通知" })}
                name="send_msg"
              >
                <RadioGroup row>
                  <FormControlLabel
                    value={1}
                    disabled={!!detailMode}
                    control={<Radio color="primary" />}
                    label={t({ defaultMessage: "是" })}
                  />
                  <FormControlLabel
                    value={0}
                    disabled={!!detailMode}
                    control={<Radio color="primary" />}
                    label={t({ defaultMessage: "否" })}
                  />
                </RadioGroup>
              </FormItem> */}
            </Container>
          </Form>
        )}
      </Paper>
    </div>
  );
};

export default Creation;
